/**
 * MyWork — Editor's only page (Phase 5 Slice B-1).
 *
 * No sidebar. Top bar with studio name + editor avatar/dropdown. Body shows
 * deliverables grouped into 4 deadline buckets: Overdue, This week, Next 2
 * weeks, Later. Each row exposes a status select (Pending / In Editing /
 * Delivered). No vendor names. No project links. No financials.
 *
 * Blind rating modal (when status → Delivered) ships in Slice B-2.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const BUCKETS = [
  { key: "overdue", label: "Overdue", accent: "rose" },
  { key: "this_week", label: "This week", accent: "champagne" },
  { key: "next_2_weeks", label: "Next 2 weeks", accent: "ink" },
  { key: "later", label: "Later", accent: "muted" },
];

const STATUSES = ["Pending", "In Editing", "Delivered"];

function formatDue(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function MyWork() {
  const { user, studio, logout, isEditor } = useAuth();
  const [activeTab, setActiveTab] = useState("active");
  const [buckets, setBuckets] = useState({ overdue: [], this_week: [], next_2_weeks: [], later: [] });
  const [completed, setCompleted] = useState({ year: null, years: [], stats: { deliverables_count: 0, projects_count: 0 }, items: [] });
  const [loading, setLoading] = useState(true);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [ratingFor, setRatingFor] = useState(null);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/editors/me/assignments");
      setBuckets({
        overdue: data.overdue || [],
        this_week: data.this_week || [],
        next_2_weeks: data.next_2_weeks || [],
        later: data.later || [],
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load your work");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompleted = useCallback(async (year) => {
    setCompletedLoading(true);
    try {
      const params = year != null ? { year } : {};
      const { data } = await api.get("/editors/me/completed", { params });
      setCompleted({
        year: data.year,
        years: data.years || [],
        stats: data.stats || { deliverables_count: 0, projects_count: 0 },
        items: data.items || [],
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load completed work");
    } finally {
      setCompletedLoading(false);
    }
  }, []);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);
  // Lazy-load completed work the first time the tab is opened, and refetch
  // it on Active-tab Delivered transitions so counts stay current.
  useEffect(() => {
    if (activeTab === "completed") fetchCompleted();
  }, [activeTab, fetchCompleted]);

  if (!isEditor) {
    // Defensive — App router should already redirect, but guard against direct nav.
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="my-work-forbidden">
        <p className="kicker">This page is for editors only.</p>
      </div>
    );
  }

  const handleStatusChange = async (deliv, newStatus) => {
    if (newStatus === deliv.status) return;
    // Phase 5 Slice B-2 — Delivered requires a blind rating first.
    if (newStatus === "Delivered") {
      setRatingFor(deliv);
      return;
    }
    // Optimistic in-place update; revert on error. NO full refetch.
    const prev = JSON.parse(JSON.stringify(buckets));
    setBuckets((b) => mutateLocalStatus(b, deliv.deliverable_id, newStatus));
    try {
      const { data } = await api.patch(
        `/editors/me/deliverables/${deliv.deliverable_id}/status`,
        { status: newStatus });
      // Phase 5 Slice B-2 realignment — server returns full updated row.
      // Reconcile in place (re-bucket if the bucket changed).
      setBuckets((b) => upsertAcrossBuckets(b, data));
      toast.success(`Marked ${newStatus}`);
    } catch (err) {
      setBuckets(prev);
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const onRatingSubmitted = (updatedRow) => {
    setRatingFor(null);
    // updatedRow is the editor-safe response from POST /vendor-ratings.
    if (updatedRow) {
      setBuckets((b) => upsertAcrossBuckets(b, updatedRow));
    }
    // If the Completed tab data is already loaded, refresh stats so the
    // tab badge count stays current without forcing a tab switch.
    if (completed.year != null) fetchCompleted(completed.year);
  };

  return (
    <div className="min-h-screen bg-brand-paper" data-testid="my-work-page">
      {/* Top bar */}
      <div className="bg-white border-b border-brand-border px-6 py-4 flex items-center justify-between" data-testid="my-work-topbar">
        <div>
          <div className="kicker" data-testid="my-work-studio-name">{studio?.name || "Studio"}</div>
          <div className="font-display text-xl" style={{ color: "var(--brand-ink)" }}>Lead Studio</div>
        </div>
        <div className="relative">
          <button onClick={() => setMenuOpen((v) => !v)}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-brand-paper"
                  data-testid="my-work-avatar-button">
            <div className="w-8 h-8 bg-brand-primary text-white flex items-center justify-center text-xs font-semibold">
              {(user?.name || "E").charAt(0).toUpperCase()}
            </div>
            <span className="text-sm" style={{ color: "var(--brand-ink)" }} data-testid="my-work-editor-name">
              {user?.name || "Editor"}
            </span>
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-brand-border w-44 shadow-lg z-20"
                 data-testid="my-work-menu">
              <button onClick={() => { setMenuOpen(false); setPwOpen(true); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-brand-paper"
                      data-testid="my-work-change-password">
                Change password
              </button>
              <button onClick={() => { setMenuOpen(false); logout().then(() => window.location.href = "/"); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-brand-paper border-t border-brand-border"
                      data-testid="my-work-logout">
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl mb-2" style={{ color: "var(--brand-ink)" }} data-testid="my-work-title">
          My work
        </h1>
        <p className="text-sm text-brand-muted mb-6" data-testid="my-work-subtitle">
          Your editing assignments grouped by deadline.
        </p>

        {/* Phase 5 polish — Active / Completed tabs */}
        {(() => {
          const activeCount = (buckets.overdue || []).length + (buckets.this_week || []).length
            + (buckets.next_2_weeks || []).length + (buckets.later || []).length;
          return (
            <div className="flex items-center gap-1 border-b border-brand-border mb-6" data-testid="my-work-tabs">
              <button onClick={() => setActiveTab("active")}
                      className={`px-4 py-2 text-xs tracking-[0.18em] uppercase border-b-2 ${activeTab === "active" ? "" : "border-transparent text-brand-muted"}`}
                      style={activeTab === "active" ? { borderColor: "var(--brand-champagne)", color: "var(--brand-ink)" } : {}}
                      data-testid="tab-active">
                Active work · {activeCount}
              </button>
              <button onClick={() => setActiveTab("completed")}
                      className={`px-4 py-2 text-xs tracking-[0.18em] uppercase border-b-2 ${activeTab === "completed" ? "" : "border-transparent text-brand-muted"}`}
                      style={activeTab === "completed" ? { borderColor: "var(--brand-champagne)", color: "var(--brand-ink)" } : {}}
                      data-testid="tab-completed">
                Completed · {completed.stats?.deliverables_count || 0}
              </button>
            </div>
          );
        })()}

        {activeTab === "active" && (
          <>
            {loading && <p className="text-sm text-brand-muted" data-testid="my-work-loading">Loading…</p>}
            {!loading && BUCKETS.map((b) => (
              <BucketSection key={b.key} label={b.label} accent={b.accent}
                              items={buckets[b.key]} onStatusChange={handleStatusChange} bucketKey={b.key} />
            ))}
            {!loading
              && BUCKETS.every((b) => (buckets[b.key] || []).length === 0)
              && <p className="text-sm italic text-brand-muted mt-6" data-testid="my-work-empty">
                  You have no editing assignments yet.
                </p>}
          </>
        )}

        {activeTab === "completed" && (
          <CompletedTab data={completed} loading={completedLoading} onYearChange={fetchCompleted} />
        )}
      </div>

      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
      {ratingFor && (
        <BlindRatingModal
          deliverable={ratingFor}
          onClose={() => setRatingFor(null)}
          onSubmitted={onRatingSubmitted}
        />
      )}
    </div>
  );
}

function mutateLocalStatus(buckets, deliverableId, newStatus) {
  const out = {};
  for (const k of Object.keys(buckets)) {
    out[k] = buckets[k].map((it) => it.deliverable_id === deliverableId ? { ...it, status: newStatus } : it);
  }
  return out;
}

// Phase 5 Slice B-2 realignment — given a fresh editor-safe deliverable row
// from the server, place it in the correct bucket (or remove entirely if
// Delivered — /my-work is the active queue, not history). Preserves order
// within the destination bucket.
function upsertAcrossBuckets(buckets, row) {
  const out = {};
  for (const k of Object.keys(buckets)) {
    out[k] = buckets[k].filter((it) => it.deliverable_id !== row.deliverable_id);
  }
  // Delivered rows are removed entirely — editors don't see completed work here.
  if (row.status === "Delivered") return out;
  const targetBucket = row.bucket || "later";
  if (!out[targetBucket]) out[targetBucket] = [];
  const inserted = [...out[targetBucket], { ...row }];
  inserted.sort((a, b) => {
    const ad = a.due_at || "\uffff";
    const bd = b.due_at || "\uffff";
    return ad.localeCompare(bd);
  });
  out[targetBucket] = inserted;
  return out;
}

function BucketSection({ label, accent, items, onStatusChange, bucketKey }) {
  if (!items || items.length === 0) return null;
  const accentColor = {
    rose: "#9F1239",
    champagne: "var(--brand-champagne)",
    ink: "var(--brand-ink)",
    muted: "var(--brand-muted)",
  }[accent] || "var(--brand-ink)";
  return (
    <section className="mb-10" data-testid={`bucket-${bucketKey}`}>
      <div className="flex items-center gap-3 mb-3">
        <h2 className="kicker" style={{ color: accentColor }} data-testid={`bucket-label-${bucketKey}`}>
          {label.toUpperCase()}
        </h2>
        <span className="text-xs text-brand-muted" data-testid={`bucket-count-${bucketKey}`}>{items.length}</span>
      </div>
      <div className="space-y-2">
        {items.map((d) => (
          <DeliverableRow key={d.deliverable_id} d={d} onStatusChange={onStatusChange} bucketKey={bucketKey} />
        ))}
      </div>
    </section>
  );
}

function DeliverableRow({ d, onStatusChange, bucketKey }) {
  return (
    <div className="bg-white border border-brand-border px-4 py-3 grid grid-cols-[1.4fr_1.4fr_0.8fr_0.9fr] gap-4 items-center"
         data-testid={`deliv-row-${d.deliverable_id}`}>
      <div className="text-sm font-medium truncate" style={{ color: "var(--brand-ink)" }} data-testid={`deliv-project-${d.deliverable_id}`}>
        {d.project_name}
      </div>
      <div className="text-sm text-brand-muted truncate" data-testid={`deliv-type-${d.deliverable_id}`}>
        {d.deliverable_type}{d.quantity != null && ` · ${d.quantity}${d.unit ? ` ${d.unit}` : ""}`}
      </div>
      <div className="text-xs text-brand-muted" data-testid={`deliv-due-${d.deliverable_id}`}>
        {formatDue(d.due_at)}
      </div>
      <select value={d.status || "Pending"}
              onChange={(e) => onStatusChange(d, e.target.value)}
              className="px-2 py-1 text-xs border border-brand-border bg-white"
              style={{ borderRadius: 0 }}
              data-testid={`deliv-status-${d.deliverable_id}`}>
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.patch("/editors/me/password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Change failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-30" data-testid="change-password-modal">
      <div className="bg-white p-6 w-[min(420px,92vw)]" style={{ borderRadius: 0 }}>
        <h3 className="font-display text-xl mb-4" style={{ color: "var(--brand-ink)" }}>Change password</h3>
        <div className="space-y-3">
          <input type="password" value={cur} onChange={(e) => setCur(e.target.value)}
                  placeholder="Current password" className="w-full px-3 py-2 text-sm border border-brand-border" style={{ borderRadius: 0 }}
                  data-testid="cp-current" />
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                  placeholder="New password (min 8 chars)" className="w-full px-3 py-2 text-sm border border-brand-border" style={{ borderRadius: 0 }}
                  data-testid="cp-new" />
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm new password" className="w-full px-3 py-2 text-sm border border-brand-border" style={{ borderRadius: 0 }}
                  data-testid="cp-confirm" />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-brand-border" data-testid="cp-cancel">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="px-3 py-2 text-sm text-white disabled:opacity-50"
                  style={{ background: "var(--brand-primary)" }} data-testid="cp-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Phase 5 Slice B-2 — blind rating modal. Vendor name NEVER appears.
// Backdrop click does NOT dismiss (only explicit Submit-success or Cancel).
// Enter inside the textarea is treated as a newline, not submit.
function BlindRatingModal({ deliverable, onClose, onSubmitted }) {
  const [stars, setStars] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (stars < 1) { setError("Please select a star rating."); return; }
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/vendor-ratings", {
        deliverable_id: deliverable.deliverable_id,
        stars,
        comment: comment.trim() || null,
      });
      toast.success("Marked Delivered");
      onSubmitted(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Submission failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4"
         style={{ background: "rgba(14,48,48,0.55)" }}
         data-testid="blind-rating-modal"
         onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
      <div className="bg-white p-7 w-[min(440px,92vw)]" style={{ borderRadius: 0 }}
           onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-xl mb-1" style={{ color: "var(--brand-ink)" }}
            data-testid="brm-title">
          Rate the raw material quality for this shoot
        </h3>
        <p className="text-xs italic text-brand-muted mb-5" data-testid="brm-subtitle">
          This rating is anonymous and helps the studio track vendor performance.
        </p>

        <div className="flex items-center gap-2 mb-4" data-testid="brm-stars"
             onMouseLeave={() => setHovered(0)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button"
                    onMouseEnter={() => setHovered(n)}
                    onClick={() => setStars(n)}
                    className="text-3xl leading-none"
                    style={{ color: (hovered || stars) >= n ? "var(--brand-champagne)" : "#D4D4D4" }}
                    data-testid={`brm-star-${n}`}>
              ★
            </button>
          ))}
          <span className="ml-2 text-xs text-brand-muted" data-testid="brm-stars-label">
            {stars > 0 ? `${stars} / 5` : "Tap to rate"}
          </span>
        </div>

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, 200))}
          rows={2}
          placeholder="Optional comment (max 200 chars)"
          className="w-full px-3 py-2 text-sm border border-brand-border bg-white mb-1"
          style={{ borderRadius: 0 }}
          data-testid="brm-comment"
        />
        <p className="text-[10px] text-brand-muted text-right mb-4">{comment.length} / 200</p>

        {error && (
          <p className="text-xs text-rose-700 mb-3" data-testid="brm-error">{error}</p>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={submitting}
                  className="px-4 py-2 text-xs tracking-[0.18em] uppercase text-brand-muted"
                  data-testid="brm-cancel">
            Cancel
          </button>
          <button onClick={submit} disabled={submitting || stars < 1}
                  className="px-4 py-2 text-xs tracking-[0.18em] uppercase text-white disabled:opacity-50"
                  style={{ background: "var(--brand-champagne)" }}
                  data-testid="brm-submit">
            {submitting ? "Submitting…" : "Submit & mark delivered"}
          </button>
        </div>
      </div>
    </div>
  );
}


// Phase 5 Slice B-2 polish — Completed tab: annual stats + reverse-chrono
// list with month headers + clickable row → popover showing rating + comment.
function CompletedTab({ data, loading, onYearChange }) {
  const [popoverFor, setPopoverFor] = useState(null);

  if (loading) {
    return <p className="text-sm text-brand-muted" data-testid="completed-loading">Loading…</p>;
  }

  const items = data.items || [];
  const groupedByMonth = items.reduce((acc, it) => {
    const k = it.month_key || "—";
    if (!acc[k]) acc[k] = [];
    acc[k].push(it);
    return acc;
  }, {});
  const orderedMonthKeys = Object.keys(groupedByMonth).sort().reverse();

  return (
    <div data-testid="completed-tab">
      {/* Stats strip */}
      <div className="bg-white border border-brand-border px-6 py-4 mb-6 flex flex-wrap items-center gap-8"
           data-testid="completed-stats">
        <div>
          <div className="kicker mb-1">DELIVERABLES</div>
          <div className="text-2xl font-display" style={{ color: "var(--brand-ink)" }}
               data-testid="completed-stat-deliverables">
            {data.stats?.deliverables_count ?? 0}
          </div>
        </div>
        <div>
          <div className="kicker mb-1">PROJECTS</div>
          <div className="text-2xl font-display" style={{ color: "var(--brand-ink)" }}
               data-testid="completed-stat-projects">
            {data.stats?.projects_count ?? 0}
          </div>
        </div>
        <div className="ml-auto">
          <div className="kicker mb-1">YEAR</div>
          <select value={data.year ?? ""} onChange={(e) => onYearChange(parseInt(e.target.value, 10))}
                  className="px-3 py-2 text-sm border border-brand-border bg-white"
                  style={{ borderRadius: 0 }} data-testid="completed-year-select">
            {(data.years || []).length === 0 && (
              <option value={data.year ?? new Date().getFullYear()}>{data.year ?? new Date().getFullYear()}</option>
            )}
            {(data.years || []).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {items.length === 0 && (
        <p className="text-sm italic text-brand-muted" data-testid="completed-empty">
          No deliveries recorded for this year yet.
        </p>
      )}

      {/* Reverse-chrono list with month headers */}
      {orderedMonthKeys.map((monthKey) => (
        <section key={monthKey} className="mb-8" data-testid={`completed-month-${monthKey}`}>
          <h2 className="kicker mb-2" data-testid={`completed-month-label-${monthKey}`}>
            {formatMonth(monthKey)}
          </h2>
          <div className="space-y-2">
            {groupedByMonth[monthKey].map((d) => (
              <button key={d.deliverable_id}
                      onClick={() => setPopoverFor(d)}
                      className="block w-full text-left bg-white border border-brand-border px-4 py-3 grid grid-cols-[1.4fr_1.4fr_0.7fr_0.7fr] gap-4 items-center hover:bg-brand-paper transition-colors"
                      data-testid={`completed-row-${d.deliverable_id}`}>
                <span className="text-sm font-medium truncate" style={{ color: "var(--brand-ink)" }}>
                  {d.project_name}
                </span>
                <span className="text-sm text-brand-muted truncate">
                  {d.deliverable_type}{d.quantity != null && ` · ${d.quantity}${d.unit ? ` ${d.unit}` : ""}`}
                </span>
                <span className="text-xs text-brand-muted">
                  {d.delivered_at ? new Date(d.delivered_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
                </span>
                <span className="text-sm" style={{ color: d.rating ? "var(--brand-champagne)" : "var(--brand-muted)" }}>
                  {d.rating ? `${"★".repeat(d.rating.stars)}${"★".repeat(5 - d.rating.stars).replace(/./g, "☆")}` : "no rating"}
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}

      {popoverFor && <CompletedRowPopover item={popoverFor} onClose={() => setPopoverFor(null)} />}
    </div>
  );
}

function formatMonth(monthKey) {
  // monthKey like "2026-06"
  if (!monthKey || monthKey === "—") return "Other";
  try {
    const [y, m] = monthKey.split("-").map((x) => parseInt(x, 10));
    return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" }).toUpperCase();
  } catch {
    return monthKey;
  }
}

function CompletedRowPopover({ item, onClose }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4"
         style={{ background: "rgba(14,48,48,0.45)" }}
         data-testid="completed-popover">
      <div className="bg-white p-6 w-[min(420px,92vw)]" style={{ borderRadius: 0 }}
           onClick={(e) => e.stopPropagation()}>
        <div className="kicker mb-2">DELIVERY DETAIL</div>
        <h3 className="font-display text-xl mb-1" style={{ color: "var(--brand-ink)" }}
            data-testid="popover-project-name">
          {item.project_name}
        </h3>
        <p className="text-xs text-brand-muted mb-4">
          {item.deliverable_type}{item.quantity != null && ` · ${item.quantity}${item.unit ? ` ${item.unit}` : ""}`} · {item.service_type}
        </p>
        <dl className="grid grid-cols-[100px_1fr] gap-2 text-xs mb-4">
          <dt className="text-brand-muted">Delivered on</dt>
          <dd style={{ color: "var(--brand-ink)" }} data-testid="popover-delivered-at">
            {item.delivered_at
              ? new Date(item.delivered_at).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })
              : "—"}
          </dd>
          <dt className="text-brand-muted">Your rating</dt>
          <dd data-testid="popover-rating">
            {item.rating
              ? <span style={{ color: "var(--brand-champagne)" }}>
                  {"★".repeat(item.rating.stars)}{"★".repeat(5 - item.rating.stars).replace(/./g, "☆")} <span className="text-brand-muted">({item.rating.stars}/5)</span>
                </span>
              : <span className="text-brand-muted italic">No rating recorded.</span>}
          </dd>
          {item.rating?.comment && (
            <>
              <dt className="text-brand-muted">Your note</dt>
              <dd className="text-brand-ink" data-testid="popover-rating-comment">
                &ldquo;{item.rating.comment}&rdquo;
              </dd>
            </>
          )}
        </dl>
        <div className="flex justify-end">
          <button onClick={onClose}
                  className="px-3 py-2 text-xs tracking-[0.18em] uppercase text-brand-muted"
                  data-testid="popover-close">Close</button>
        </div>
      </div>
    </div>
  );
}

